import {
  Button,
  Modal,
  ModalContext,
  Spinner,
  toast,
  ViewerContext,
} from "dash-ui";
import {
  useChangeCurrentProductSubscriptionMutation,
  useGetCurrentStripeAccountLazyQuery,
  useGetPreviewInvoiceLazyQuery,
  useScheduleDowngradeMutation,
} from "gql/generated.graphql";
import { useContext, useEffect, useState } from "react";
import PromoCodeInput from "../PromoCodeInput/PromoCodeInput";
import { formatMillisToDate } from "../../ProfileBilling.helpers";

const REST_API_DOMAIN = process.env["REACT_APP_REST"];

// Main component
export default function InvoicePreviewModal() {
  const { account } = useContext(ViewerContext);
  const { isOpenCurrent, payload, toggleModal } = useContext(ModalContext);

  // Destructure payload to apply correct types.
  const {
    mode,
    newStripePriceId,
    newSubscriptionProductId,
  }: {
    mode: "upgrade" | "downgrade";
    newStripePriceId: string;
    newSubscriptionProductId: string;
  } = payload || {};

  const isOpen = !!isOpenCurrent["InvoicePreviewModal"];
  const closeModal = () => {
    toggleModal(false, "InvoicePreviewModal");
  };

  // State from PromoCodeInput
  const [promoCode, setPromoCode] = useState<string | undefined>();

  const [getStripeAccountLazyQuery, { data: stripeAccountData }] =
    useGetCurrentStripeAccountLazyQuery();
  const stripeAccount = stripeAccountData?.getCurrentStripeAccount;
  const defaultPaymentMethod = stripeAccount?.paymentMethods?.at(0);

  const [
    getPreviewInvoiceLazyQuery,
    { data, loading: loadingInvoice, error, refetch },
  ] = useGetPreviewInvoiceLazyQuery({
    fetchPolicy: "no-cache",
    variables: {
      input: {
        mode: mode,
        newStripePriceId: newStripePriceId,
        promoCode: promoCode,
      },
    },
  });
  const invoice = data?.getPreviewInvoice;
  const invoiceCoupon = data?.getPreviewInvoice.coupon;

  const [
    changeSubscriptionMutation,
    { loading: isChangeProductSubscriptionLoading },
  ] = useChangeCurrentProductSubscriptionMutation();

  const [scheduleDowngradeMutation, { loading: isDowngradeLoading }] =
    useScheduleDowngradeMutation();

  function handleSubmit() {
    if (!newSubscriptionProductId || !newStripePriceId) return;

    if (mode === "upgrade") {
      changeSubscriptionMutation({
        variables: {
          input: {
            subscriptionProductId: newSubscriptionProductId,
            stripeCouponId: promoCode,
          },
        },
        onCompleted: () => {
          closeModal();
          toast.success("Your subscription was upgraded!");
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    } else {
      scheduleDowngradeMutation({
        variables: {
          input: {
            newStripePriceId: newStripePriceId,
          },
        },
        onCompleted: () => {
          closeModal();
          toast.success("Your subscription is scheduled to downgrade.");
        },
        onError: (error) => {
          toast.error(error.message);
        },
      });
    }
  }

  function handleValidateCoupon(promoCode?: string) {
    setPromoCode(promoCode);
    refetch();
  }

  useEffect(() => {
    setPromoCode(undefined);
    if (isOpen && payload) {
      getStripeAccountLazyQuery();
      getPreviewInvoiceLazyQuery();
    }
  }, [isOpen, payload]);

  return (
    <Modal
      closeIcon
      w="fit-content"
      isOpen={isOpen}
      onRequestClose={closeModal}
      title="Confirm your subscription changes">
      {loadingInvoice && (
        <div className="flex flex-row justify-center w-full mt-4 h-16 items-center">
          <Spinner />
        </div>
      )}

      {error && !loadingInvoice && (
        <div className="text-sm my-4">
          ⚠️ There was a problem while fetching your data. Please try again
          later or contact support.
        </div>
      )}

      {!error && (
        <div className="text-sm w-full mt-4">
          {!loadingInvoice && (
            <InvoiceItems>
              {invoice?.items.map((item) => (
                <InvoiceItem key={item.id} item={item} />
              ))}

              <InvoiceCoupon invoiceCoupon={invoiceCoupon} />

              <AmountDue
                mode={mode}
                dueDateMillis={invoice?.dueDateMillis}
                amountDue={invoice?.amountDue}
              />

              <PaymentMethod paymentMethod={defaultPaymentMethod} />
            </InvoiceItems>
          )}

          {mode === "upgrade" && (
            <div>
              <hr className="mb-4 border-slate-200"></hr>
              <h2 className="font-bold">Promo code</h2>
              <PromoCodeInput
                onValidateCoupon={handleValidateCoupon}
                subscriptionProductId={newSubscriptionProductId}
              />
              <hr className="mt-4 border-slate-200"></hr>
            </div>
          )}

          {!defaultPaymentMethod && !loadingInvoice && (
            <PaymentMethodWarning
              stripeCustomerId={account?.stripeCustomerId}
            />
          )}

          {mode === "downgrade" && !loadingInvoice && (
            <ScheduleWarning periodEnd={invoice?.dueDateMillis} />
          )}

          <div className="flex flex-row gap-2 justify-end w-full mt-6">
            <Button
              onClick={closeModal}
              smplus
              colorVariation="danger"
              variation="outline">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              smplus
              isLoading={
                isChangeProductSubscriptionLoading || isDowngradeLoading
              }
              disabled={!defaultPaymentMethod || loadingInvoice || !!error}>
              {mode === "upgrade" ? "Upgrade" : "Schedule Downgrade"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ----------------- Helper components -----------------

function PaymentMethodWarning({ stripeCustomerId }) {
  return (
    <p className="mb-4">
      ⚠️ Please{" "}
      <a
        className={`text-sm hover:opacity-80 underline`}
        href={`${REST_API_DOMAIN}/stripe/create-portal-session?customerId=${stripeCustomerId}`}>
        add a payment method
      </a>{" "}
      before continuing.
    </p>
  );
}

function InvoiceItem({ item }) {
  return (
    <tr key={item.id}>
      <td className="py-2">{item.description}</td>
      <td
        className={`py-2 text-right ${
          item.amount < 0 ? "text-green-600" : ""
        }`}>
        {item.amount < 0 ? "-" : ""}${Math.abs(item.amount)}
      </td>
    </tr>
  );
}

function InvoiceCoupon({ invoiceCoupon }) {
  if (!invoiceCoupon) return <></>;

  return (
    <tr>
      <td className="py-2">{invoiceCoupon.name}</td>
      <td className={`py-2 text-right text-green-600`}>
        {invoiceCoupon?.amountOff && (
          <span>-${Math.abs(invoiceCoupon?.amountOff / 100)} OFF</span>
        )}
        {invoiceCoupon?.percentOff && (
          <span>-{Math.abs(invoiceCoupon?.percentOff)}% OFF</span>
        )}
      </td>
    </tr>
  );
}

function AmountDue({
  mode,
  amountDue,
  dueDateMillis,
}: {
  mode: "upgrade" | "downgrade";
  amountDue?: number;
  dueDateMillis?: number;
}) {
  return (
    <tr className="border-t">
      <td className="py-2">
        {mode === "upgrade" && "Amount due today"}
        {mode === "downgrade" &&
          `Amount due starting ${formatMillisToDate(dueDateMillis)}`}
      </td>
      <td className="py-2 text-right font-semibold">${amountDue}</td>
    </tr>
  );
}

function PaymentMethod({ paymentMethod }) {
  return (
    <tr className="border-t">
      <td className="py-2">Payment Method</td>
      <td className="py-2 text-right">
        {paymentMethod?.cardBrand.toUpperCase()} *
        {paymentMethod?.cardNumber.slice(-4)}
      </td>
    </tr>
  );
}

function InvoiceItems({ children }) {
  return (
    <table className="w-full border-spacing-4">
      <thead className="font-bold border-b text-base h-8">
        <tr>
          <th className="text-start">Items</th>
        </tr>
      </thead>
      <tbody className="">{children}</tbody>
    </table>
  );
}

function ScheduleWarning({ periodEnd }) {
  if (!periodEnd) return <></>;
  return (
    <p className="my-2">
      Your current plan will remain active until{" "}
      <span className="font-semibold">{formatMillisToDate(periodEnd)}</span>.
    </p>
  );
}
